import { toDate } from "@/utils/utils";
import dayjs from "dayjs";
import { Role } from "../../common/security/enums";
import type { AgentUserAccount, ClientUserAccount, User } from "../user/types";
import { TotpCodeChannel } from "./enums";
import type { AuthenticatedLoginResponse, AuthSelectedAccount, AuthToken, LoginResponse } from "./types";

export const isAuthTokenValid = (authToken?: AuthToken): authToken is AuthToken =>
  !!authToken && !!toDate(authToken.validUntil)?.isAfter(dayjs());

export const isAuthenticatedLoginResponse = (response: LoginResponse): response is AuthenticatedLoginResponse =>
  !!response.token && !!response.tokenValidUntil && !!response.user;

export const isTotpCodeChannel = (value?: string): value is TotpCodeChannel =>
  !!value && Object.values<string>(TotpCodeChannel).includes(value);

export const isActiveUserAccount = (account: AgentUserAccount | ClientUserAccount) =>
  account.confirmed && !account.disabled;

export const getRoleUserAccounts = (user: User | undefined, role?: Role) =>
  (role === Role.AGENT ? user?.agentUserRole?.userAccounts : user?.clientUserRole?.userAccounts) ?? [];

export const findUserAccount = (user: User | undefined, { role, accountId }: AuthSelectedAccount) =>
  getRoleUserAccounts(user, role).find((account) => account.id === accountId);

/**
 * The agent role wins whenever an active agent account exists: the client portal offers no way to switch roles,
 * so a remembered client account would otherwise lock the user out of the agent portal.
 */
export const resolveSelectedAccount = (user: User, previous: AuthSelectedAccount): AuthSelectedAccount => {
  // TODO(multi-tenant) let a user with both an agent and a client role choose which one to log in with
  const role = getRoleUserAccounts(user, Role.AGENT).some(isActiveUserAccount) ? Role.AGENT : Role.CLIENT;
  const activeAccounts = getRoleUserAccounts(user, role).filter(isActiveUserAccount);

  const isPreviousReusable =
    previous.userId === user.id &&
    previous.role === role &&
    activeAccounts.some((account) => account.id === previous.accountId);

  return isPreviousReusable ? previous : { userId: user.id, role, accountId: activeAccounts[0]?.id };
};
